import { useEffect, useMemo, useState } from "react";
import { Button, Col, Modal, Radio, Row, Select, Tooltip } from "antd";
import { FaAddressCard } from "react-icons/fa6";
import { HiArrowPath } from "react-icons/hi2";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { PlusOutlined } from "@ant-design/icons";
import { GlobalTable } from "./GlobalTable";
import useGet from "../hooks/useGet";
import { hasRole } from "../hooks/getUserRole";
import { getModifiedResponse } from "../utils/responseModifier";
import {
  getOptionLabel,
  itemOptions as staticItemOptions,
  termOptions,
} from "../../utils/Common";
import { LicenseCodeForm } from "../../modules/LicenseCode/Components/LicenseCodeForm";

const initialFilterState = {
  Item: undefined,
  CodeNo: undefined,
  Term: undefined,
};

const FILTER_PARAM_KEYS = ["Item", "CodeNo", "Term"];

const initialParams = {
  limit: 10,
  page: 1,
  sort_by: "Item",
  order: "asc",
};

const hasValue = (value) => value != null && value !== "";

export const getCodeKey = (code) => String(code?.CodeNo);

export const formatMoney = (value) => `$ ${(Number(value) || 0).toFixed(2)}`;

const getColumns = ({ columns, isSelected, getDisabledReason, onToggle }) => [
  {
    title: "Item",
    dataIndex: "ItemName",
    key: "ItemName",
    width: 140,
    ellipsis: true,
    render: (_, record) => record?.ItemName || record?.Item || "",
  },
  {
    title: "Code",
    dataIndex: "CodeName",
    key: "CodeName",
    width: 130,
    ellipsis: true,
    render: (_, record) => record?.CodeName || record?.Code || "",
  },
  {
    title: "Term",
    dataIndex: "Term",
    key: "Term",
    width: 100,
    render: (value) =>
      hasValue(value)
        ? getOptionLabel(termOptions, Number(value)) || value
        : "",
  },
  {
    title: "Price",
    dataIndex: "Price",
    key: "Price",
    width: 90,
    render: (value) => formatMoney(value),
  },
  ...columns,
  {
    title: "",
    key: "select",
    width: 44,
    align: "center",
    render: (_, record) => {
      const disabledReason = getDisabledReason?.(record);
      return (
        <Tooltip title={disabledReason || ""}>
          {/* span keeps the tooltip working while the radio is disabled */}
          <span className="inline-flex">
            <Radio
              checked={isSelected(record)}
              disabled={Boolean(disabledReason)}
              onClick={() => onToggle(record)}
            />
          </span>
        </Tooltip>
      );
    },
  },
];

/**
 * Paged license code list with the Item/Code/Term filter and a select radio per row.
 * Selection state lives in the parent (isSelected/onToggle).
 */
export const LicenseCodeSelectTable = ({
  columns = [],
  isSelected,
  getDisabledReason,
  onToggle,
  onCodeCreated,
}) => {
  const [listData, setListData] = useState([]);
  const [filter, setFilter] = useState(initialFilterState);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [params, setParams] = useState(initialParams);
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const canCreateCode = hasRole(1, 2);

  const query = new URLSearchParams(params).toString();
  const url = `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/?${query}`;
  const { data, loading, refetch } = useGet(url);

  const optionUrl = `${process.env.REACT_APP_API_URL}/api/licensecodes/getoptions/`;
  const { data: optionData, loading: optionDataLoading } = useGet(optionUrl);
  const itemOptions = useMemo(() => {
    const apiItems = getModifiedResponse(optionData, "items");
    const known = new Set(staticItemOptions.map((opt) => Number(opt.value)));
    return [
      ...staticItemOptions,
      ...apiItems.filter((opt) => !known.has(Number(opt.value))),
    ];
  }, [optionData]);

  const codesUrl = hasValue(filter.Item)
    ? `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/?limit=0&sort_by=Item&order=asc&Item=${filter.Item}`
    : null;
  const { data: codesData, loading: codesLoading } = useGet(codesUrl);
  const codeOptions = useMemo(
    () =>
      (codesData?.data ?? []).map((item) => ({
        label: item.CodeName || String(item.Code),
        value: item.CodeNo,
      })),
    [codesData],
  );

  useEffect(() => {
    if (data && !loading) {
      setListData(
        (data?.data ?? []).map((el) => ({ ...el, key: getCodeKey(el) })),
      );
      const totalCount = data?.count ?? data?.total;
      if (typeof totalCount === "number") {
        setPagination((prev) => ({ ...prev, total: totalCount }));
      }
    }
  }, [data, loading]);

  const paginationConfig = {
    current: pagination.current,
    pageSize: pagination.pageSize,
    total: pagination.total,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination((prev) => ({ ...prev, current: page, pageSize }));
      setParams((prev) => ({ ...prev, limit: pageSize, page }));
    },
  };

  const handleFilter = () => {
    const filterDict = {};
    for (const key of FILTER_PARAM_KEYS) {
      if (hasValue(filter[key])) filterDict[key] = filter[key];
    }
    setPagination((prev) => ({ ...prev, current: 1 }));
    setParams((prev) => {
      const next = { ...prev, page: 1 };
      FILTER_PARAM_KEYS.forEach((key) => delete next[key]);
      return { ...next, ...filterDict };
    });
  };

  const handleReset = () => {
    setFilter(initialFilterState);
    setPagination({ current: 1, pageSize: 10, total: 0 });
    setParams(initialParams);
  };

  const handleCodeCreated = (created) => {
    setCodeModalOpen(false);
    if (hasValue(created?.Item)) {
      setFilter({ ...initialFilterState, Item: Number(created.Item) });
      setPagination((prev) => ({ ...prev, current: 1 }));
      setParams((prev) => {
        const next = { ...prev, page: 1 };
        FILTER_PARAM_KEYS.forEach((key) => delete next[key]);
        return { ...next, Item: Number(created.Item) };
      });
    }
    refetch?.();
    onCodeCreated?.(created);
  };

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={12} lg={8} xl={6}>
          <Select
            showSearch
            allowClear
            size="small"
            placeholder="Item"
            optionFilterProp="label"
            options={itemOptions}
            loading={optionDataLoading}
            value={hasValue(filter.Item) ? Number(filter.Item) : undefined}
            onChange={(value) =>
              setFilter((prev) => ({ ...prev, Item: value, CodeNo: undefined }))
            }
            style={{ width: "100%" }}
          />
        </Col>
        <Col xs={24} sm={24} md={12} lg={8} xl={6}>
          <Select
            showSearch
            allowClear
            size="small"
            placeholder="Code"
            optionFilterProp="label"
            options={codeOptions}
            loading={codesLoading}
            disabled={!hasValue(filter.Item)}
            value={filter.CodeNo}
            onChange={(value) =>
              setFilter((prev) => ({ ...prev, CodeNo: value }))
            }
            style={{ width: "100%" }}
          />
        </Col>
        <Col xs={24} sm={24} md={12} lg={8} xl={6}>
          <Select
            showSearch
            allowClear
            size="small"
            placeholder="Term"
            optionFilterProp="label"
            options={termOptions}
            value={hasValue(filter.Term) ? Number(filter.Term) : undefined}
            onChange={(value) =>
              setFilter((prev) => ({ ...prev, Term: value }))
            }
            style={{ width: "100%" }}
          />
        </Col>
      </Row>

      <div className="flex items-center my-4 flex-nowrap gap-2">
        <Button
          type="primary"
          className="mr-2 w-[104px] h-[30px] font-medium"
          onClick={handleFilter}
        >
          <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
          Apply
        </Button>
        <Button
          className="reset-button w-[104px] h-[30px] font-medium"
          onClick={handleReset}
        >
          <HiArrowPath className="!text-[14px] font-medium" />
          Reset
        </Button>
        {canCreateCode ? (
          <Button
            type="primary"
            className="min-w-[104px] h-[30px] font-medium"
            onClick={() => setCodeModalOpen(true)}
          >
            <PlusOutlined />
            New Code
          </Button>
        ) : null}
      </div>

      <GlobalTable
        columns={getColumns({
          columns,
          isSelected,
          getDisabledReason,
          onToggle,
        })}
        dataSource={listData}
        pagination={paginationConfig}
        loading={loading}
      />

      <Modal
        open={codeModalOpen}
        closable={false}
        onCancel={() => setCodeModalOpen(false)}
        footer={null}
        width={580}
        centered
        destroyOnClose
        title={
          <div className="flex items-center bg-gray-200 p-2 rounded-md">
            <FaAddressCard className="text-[15px] text-black ml-1" />
            <span className="text-[14px] font-[Poppins] text-black ml-2">
              Add Code
            </span>
          </div>
        }
      >
        <LicenseCodeForm
          isModalForm
          defaultValues={
            hasValue(params.Item) ? { Item: Number(params.Item) } : undefined
          }
          onCancel={() => setCodeModalOpen(false)}
          onCreated={handleCodeCreated}
        />
      </Modal>
    </>
  );
};
